import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DOMParser } from 'prosemirror-model';
import { EditorState, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

// eslint-disable-next-line no-unused-vars
import styles from './TextEditor.module.scss';
import { Toolbar } from './Toolbar';
import configureEditor from './config';
import { packageNames } from './packages';
import EmojiButton from './packages/emoji/components/EmojiButton';
import ImageButton from './packages/image/components/ImageButton';

const {
	ALIGN,
	BOLD,
	ITALIC,
	UNDERLINE,
	HEADING,
	HISTORY,
	LIST,
	LINK,
	IMAGE,
	MENTION,
	EMOJI,
	MERGE_TAG,
	SUBMIT,
} = packageNames;

const propTypes = {

	/**
	 * The id of the DOM element where the editor will be injected.
	 */
	editorDomId: PropTypes.string.isRequired,

	/**
	 * Initial content to show in editor. Can either be a JSON-serialized
	 * ProseMirror Document object or a DOM node.
	 */
	content: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.instanceOf(Element)]),

	/**
	 * Flag specifying if editor is read-only.
	 */
	readOnly: PropTypes.bool.isRequired,

	/**
	 * Flag specifying if toolbar should be shown.
	 */
	showToolbar: PropTypes.bool.isRequired,

	/**
	 * The toolbar position.
	 */
	toolbarPosition: PropTypes.oneOf(['top', 'bottom']).isRequired,

	/**
	 * Flag specifying if chat view should be rendered.
	 */
	chatView: PropTypes.bool.isRequired,

	/**
	 * Flag specifying if mobile view should be rendered.
	 */
	mobileView: PropTypes.bool.isRequired,

	/**
	 * Flag specifying if modal view should be rendered.
	 */
	modalView: PropTypes.bool.isRequired,

	/**
	 * Flag specifying if input should be focused when mounted.
	 */
	autoFocus: PropTypes.bool.isRequired,

	/**
	 * The users that are available to at-mention.
	 *
	 * NOTE: Map from userID -> user, enrollment, and avatar data.
	 *
	 * NOTE: Only required if using "mention" plugin.
	 */
	mentionUsers: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The user id.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user display name.
		 */
		display_name: PropTypes.string.isRequired,

		/**
		 * An array of avatars (profile pictures).
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			image: PropTypes.string,

			signature: PropTypes.string,

			policy: PropTypes.string,

		})).isRequired,

	})),

	/**
	 * List of handlers for specific content events.
	 */
	triggers: PropTypes.shape({

		/**
		 * Callback function to handle clicking a link.
		 */
		onLinkClick: PropTypes.func,

		/**
		 * Callback function to handle the editor receiving focus.
		 */
		onEditorFocus: PropTypes.func,

		/**
		 * Callback function to handle the editor losing focus.
		 */
		onEditorBlur: PropTypes.func,

		/**
		 * Callback function to handle the toolbar losing focus.
		 */
		onToolbarBlur: PropTypes.func,

		/**
		 * Callback function to handle the profile modal trigger for at mentions.
		 */
		onMentionClick: PropTypes.func,

	}),

	/**
	 * The style string for the width and height of emojis.
	 */
	emojiSize: PropTypes.string,

	/**
	 * The text to display when there is no content.
	 */
	placeholderText: PropTypes.string,

	/**
	 * Configuration settings for editor features.
	 */
	config: PropTypes.shape({

		/**
		 * Bold formatting.
		 */
		[BOLD]: PropTypes.bool,

		/**
		 * Italics formatting.
		 */
		[ITALIC]: PropTypes.bool,

		/**
		 * Underline formatting.
		 */
		[UNDERLINE]: PropTypes.bool,

		/**
		 * Heading formatting.
		 */
		[HEADING]: PropTypes.bool,

		/**
		 * Undo and redo.
		 */
		[HISTORY]: PropTypes.bool,

		/**
		 * Bullet and number lists.
		 */
		[LIST]: PropTypes.bool,

		/**
		 * Link embedding.
		 */
		[LINK]: PropTypes.bool,

		/**
		 * Inline images.
		 */
		[IMAGE]: PropTypes.bool,

		/**
		 * At-mentions.
		 */
		[MENTION]: PropTypes.bool,

		/**
		 * Emojis.
		 */
		[EMOJI]: PropTypes.bool,

		/**
		 * Email Merge Tags.
		 */
		[MERGE_TAG]: PropTypes.bool,

		/**
		 * Submission via "Enter" key.
		 */
		[SUBMIT]: PropTypes.bool,

	}),

	/**
	 * Custom class name for the editor container.
	 */
	className: PropTypes.string,

	/**
	 * Custom class name for the editor body.
	 */
	innerClassName: PropTypes.string,

	/**
	 * Custom class name for the editor toolbar.
	 */
	toolbarClassName: PropTypes.string,

	/**
	 * The scope of the editor content.
	 *
	 * NOTE: Used to open links/at-mentions via the TC Link API.
	 */
	scope: PropTypes.string,

	/**
	 * The scope ID of the editor content.
	 *
	 * NOTE: Used to open links/at-mentions via the TC Link API.
	 */
	scopeId: PropTypes.string,

	/**
	 * Filestack credentials object for uploading images.
	 */
	filestackSettings: PropTypes.shape({

		apiKey: PropTypes.string,

		security: PropTypes.shape({

			policy: PropTypes.string,

			signature: PropTypes.string,

		}),

	}),

	/**
	 * Set of merge tags that are allowed in links.
	 */
	mergeTags: PropTypes.shape({

		organization: PropTypes.shape({

			domain: PropTypes.bool,

		}),

		invitation: PropTypes.shape({

			id: PropTypes.bool,

		}),

	}),

	/**
	 * Optional callback function to handle updates to the text editor.
	 */
	onChange: PropTypes.func,

	/**
	 * Optional callback function to handle submission of the
	 * text editor via "Enter" keypress.
	 */
	onSubmit: PropTypes.func,

};

const defaultProps = {
	showToolbar: true,
	toolbarPosition: 'top',
	readOnly: false,
	chatView: false,
	mobileView: false,
	modalView: false,
	autoFocus: false,
	config: {
		[BOLD]: true,
		[ITALIC]: true,
		[UNDERLINE]: true,
		[ALIGN]: true,
		[HISTORY]: true,
		[LIST]: true,
		[LINK]: true,
		[IMAGE]: true,
	},
};

/**
 * Shared component to render the ProseMirror WYSIWYG editor.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
class TextEditor extends Component {

	constructor(props) {

		super(props);

		const {
			config,
			content,
			emojiSize,
			placeholderText,
			chatView,
			mentionUsers,
			scope,
			scopeId,
			triggers,
			onSubmit,
		} = props;

		const onMentionClick = triggers?.onMentionClick;

		const { schema, plugins, ui } = configureEditor(config, {
			emojiSize,
			placeholderText,
			chatView,
			mentionUsers,
			scope,
			scopeId,
			onSubmit,
			onMentionClick,
		});

		const parser = DOMParser.fromSchema(schema);

		let doc = content;

		let editorState;

		try {

			// Check for DOM node
			if (content?.nodeType) {
				doc = parser.parse(content, { preserveWhitespace: true }).toJSON();
			}

			// Re-configure initial content to new schema reference
			// https://github.com/ProseMirror/prosemirror/issues/754
			doc = doc && schema.nodeFromJSON(doc);

			editorState = EditorState.create({
				schema,
				plugins,
				doc,
			});

		} catch (error) {

			console.error(error);

		}

		this.state = {
			editorState,
		};

		this.editorView = null;
		this.tooltips = ui.tooltips;
		this.toolbarButtons = ui.toolbarButtons;

		this.rootContainer = React.createRef();

		this.dispatchTransaction = this.dispatchTransaction.bind(this);
		this.focus = this.focus.bind(this);

	}

	componentDidMount() {

		const { editorDomId, readOnly, modalView, autoFocus, triggers } = this.props;
		const { editorState } = this.state;

		// Don't do anything if we were unable to initialize the editor state
		if (!editorState) {
			return;
		}

		// Set up editor view
		this.editorDOM = document.getElementById(editorDomId);

		// Configure the editor view props
		const editorViewProps = {
			state: editorState,
			// This allows us to hijack the transaction for any state change
			dispatchTransaction: this.dispatchTransaction,
			// Check if the editor is read only
			editable: () => !readOnly,
			// Check if the editor focus state should trigger callbacks
			handleDOMEvents: {
				focus: triggers && triggers.onEditorFocus,
				blur: triggers && triggers.onEditorBlur,
			},
		};

		// Check if the editor is being used in a popup modal
		if (modalView) {

			// If so, we need to ignore auto-scrolling effects on the window...
			// By returning true here, we can prevent the prosemirror auto-scroll logic
			editorViewProps.handleScrollToSelection = () => true;

		}

		// Initialize the editor view
		this.editorView = new EditorView(this.editorDOM, editorViewProps);

		// Set up link click handler if provided
		if (readOnly && triggers && triggers.onLinkClick) {

			// Get all link elements within the text editor doc
			const linkElements = document.querySelectorAll(`#${editorDomId} a`);

			for (let i = 0; i < linkElements.length; i++) {

				const linkEl = linkElements[i];

				linkEl.addEventListener('click', triggers.onLinkClick);

			}

		}

		if (autoFocus) {

			this.focus();

		}

		// Force re-render to use updated editorView
		this.forceUpdate();

	}

	componentDidUpdate(prevProps) {

		const { readOnly, autoFocus } = this.props;

		if (prevProps.readOnly !== readOnly) {

			// Reconfigure editor view
			this.editorView.setProps({
				editable: () => !readOnly,
			});

			if (autoFocus && !readOnly) {

				// Re-focus on the input
				this.focus();

				// Set selection at the end of the document
				const { tr, doc } = this.editorView.state;

				tr.setSelection(TextSelection.near(doc.resolve(doc.content.size)));

				this.editorView.dispatch(tr);

			}

		}

	}

	focus() {

		if (this.editorView && this.editorView.editable) {

			this.editorView.focus();

		}

	}

	dispatchTransaction(tr) {

		const { onChange } = this.props;
		const { editorState } = this.state;

		// eslint-disable-next-line prefer-reflect
		const nextState = editorState.apply(tr);

		// Update view and state based on latest transaction
		this.editorView.updateState(nextState);
		this.setState({ editorState: nextState });

		if (onChange) {

			onChange(nextState.doc.toJSON());

		}

	}

	render() {

		const {
			editorDomId,
			showToolbar,
			toolbarPosition,
			readOnly,
			chatView,
			mobileView,
			mentionUsers,
			filestackSettings,
			className,
			innerClassName,
			toolbarClassName,
			config,
			mergeTags,
			triggers,
		} = this.props;

		// Don't render anything if we were unable to initialize the editor state
		if (!this.state.editorState) {
			return null;
		}

		const editableStyle = readOnly ? '' : styles.editable;
		const menuStyle = (showToolbar && !readOnly) ? styles['with-menu'] : '';

		let toolbar;

		if (this.editorView && showToolbar && !readOnly) {

			toolbar = (
				<Toolbar
					id={`${editorDomId}-toolbar`}
					editorDomId={editorDomId}
					className={toolbarClassName}
					onBlur={triggers && triggers.onToolbarBlur}>
					{this.toolbarButtons
						// Exclude emoji button for mobile
						.filter(({ key }) => key !== 'EmojiButton' || !mobileView)
						.map(({ Button, key }) => (
							<Button
								key={key}
								editorView={this.editorView}
								editorDomId={editorDomId}
								filestack={filestackSettings}
								mergeTags={mergeTags} />
						))
					}
				</Toolbar>
			);

		}

		return (
			<div
				ref={this.rootContainer}
				className={`${className || ''} ${styles['text-editor-container']}`}>
				{toolbarPosition === 'top' && toolbar}
				<div
					className={`d-flex ${innerClassName || ''} ${styles['text-editor-body']} ${editableStyle} ${menuStyle}`}>
					<div
						id={editorDomId}
						className="w-100">
					</div>
					{this.editorView && !showToolbar && !readOnly && config[IMAGE] &&
						<ImageButton
							editorDomId={editorDomId}
							editorView={this.editorView}
							filestack={filestackSettings}
							inline />
					}
					{this.editorView && !showToolbar && !readOnly && !mobileView && config[EMOJI] &&
						<EmojiButton
							id={`emoji-button-${editorDomId}`}
							editorDomId={editorDomId}
							editorView={this.editorView}
							rootContainer={this.rootContainer}
							inline />
					}
				</div>
				{toolbarPosition === 'bottom' && toolbar}
				{this.editorView &&
						this.tooltips.map(({ Tooltip, key, showOnReadOnly }) => {

							if ((showOnReadOnly && !readOnly) ||
								(!showOnReadOnly && readOnly)) {
								return undefined;
							}

							return (
								<Tooltip
									key={key(this.editorView.state)}
									editorView={this.editorView}
									editorContainer={this.editorDOM}
									rootContainer={this.rootContainer}
									chatView={chatView}
									mobileView={mobileView}
									mentionUsers={mentionUsers}
									mergeTags={mergeTags} />
							);

						})
				}
			</div>
		);

	}

}

TextEditor.propTypes = propTypes;
TextEditor.defaultProps = defaultProps;

export default TextEditor;
